import { useEffect, useMemo, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useCreateComponentMutation } from '../api/authoring'
import type { Document, Manifest } from '../api/authoring'
import { problemMessage } from '../api/baseApi'
import { shown } from '../api/useIntent'
import { creationFields, creationSchema } from '../model/create'
import { fieldsSchema, typeProse, uploadsInto } from '../model/manifest'
import { SchemaForm } from '../editors/SchemaForm'

/**
 * Bringing a component into being.
 *
 * `POST …/components` has been built, generated into this client's API
 * layer and called by nothing, which is the gap the goal doc named: an
 * author could place what already existed and define a child inline, and
 * could not bring a standalone Content, ContentGroup, Gallery or Media
 * into the world. The picker's "every one in this site is defined inline
 * — try the other tab" was that absence surfacing as an empty list.
 *
 * **Creation is the library's operation**, and this dialog is reached
 * from the libraries and from the picker. Placing what it makes is the
 * separate, existing step, which is right: a standalone component's whole
 * point is that it may be placed nowhere, or in five places.
 *
 * **What it must never do is follow a create with a list.** Enumeration
 * is the one authoring read served from the projection, so it lags; the
 * create answers with the whole document at its first revision precisely
 * so nothing has to go looking for what it just made. That answer is
 * handed to `onCreated` and written into the document cache by the API
 * layer, so the caller can navigate to it or place it immediately.
 */
export function CreateComponentDialog({
  tenant,
  open,
  manifest,
  types,
  onClose,
  onCreated,
}: {
  tenant: string
  open: boolean
  manifest: Manifest | undefined
  /**
   * The types this dialog may create. A library passes its own; the
   * picker passes the holding node's palette, so the thing created is one
   * that can then be placed where the author was standing.
   */
  types: string[]
  onClose: () => void
  /** The created document, at revision 1. */
  onCreated: (document: Document) => void
}) {
  const [create, { isLoading, error, reset }] = useCreateComponentMutation()
  const [typeName, setTypeName] = useState(types[0] ?? '')
  const [fields, setFields] = useState<Record<string, unknown>>({})

  // Reset on open, not on mount: the dialog stays mounted between uses,
  // so initialising once would leave the previous creation's half-typed
  // fields in the form the next time it is opened.
  //
  // `reset` is read through a ref rather than listed: it changes when a
  // request starts, and as a dependency it cleared the form the moment
  // Create was pressed, so a refusal had nothing left to explain. See
  // AddPageDialog.
  const resetMutation = useRef(reset)
  resetMutation.current = reset
  useEffect(() => {
    if (!open) return
    setTypeName(types[0] ?? '')
    setFields({})
    resetMutation.current()
  }, [open, types])

  // Cleared on a type change for the same reason: the properties offered
  // are the new type's, and carrying a value across would send a Media a
  // Content's body under a name it happens to share.
  const schema = useMemo(() => creationSchema(manifest, typeName), [manifest, typeName])
  const root = useMemo(() => fieldsSchema(manifest, typeName), [manifest, typeName])

  const submit = async () => {
    const created = await create({
      tenant,
      // No id: the platform mints one, a uuid, and a create has no way to
      // state another. The dialog once offered an Id box and sent what was
      // typed, and the platform ignored it.
      createComponent: {
        type: typeName,
        fields: creationFields(fields),
      },
    }).unwrap()
    onCreated(created)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{types.length > 1 ? 'Make something new' : `New ${typeProse(manifest, typeName).title.toLowerCase()}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {types.length > 1 ? (
            <TextField
              select
              label="Type"
              value={typeName}
              onChange={(event) => {
                setTypeName(event.target.value)
                setFields({})
              }}
              sx={{ maxWidth: 240 }}
            >
              {types.map((name) => (
                <MenuItem key={name} value={name}>
                  {typeProse(manifest, name).title}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography variant="body2" color="text.secondary">
              A new <strong>{typeProse(manifest, typeName).title.toLowerCase()}</strong>.
            </Typography>
          )}

          {schema && root && (
            <SchemaForm schema={schema} root={root} value={fields} onChange={setFields} disabled={isLoading} />
          )}

          {/* **Asked of the manifest and of what the author has chosen so
              far**, never of the type name — which is the same rule the
              uploader itself follows, so a second type that holds a file
              says this the day it is registered.

              It is here because the absence is the one thing an author
              reasonably reads as a gap. A create form that names a kind
              of media and then offers nowhere to put the file looks
              unfinished, and it is not — an upload is presigned against a
              component, so there has to *be* one first. Saying so costs a
              sentence and turns a missing control into a next step.

              And it is asked of the *fields* because a type may hold a
              file for only some of its documents. Promising a file over
              a kind that points at somebody else's URL was promising a
              step the surface refuses a grant for — and the address it
              does take is a box on this very form. */}
          {uploadsInto(manifest, typeName, fields) && (
            <Alert severity="info">
              The file comes next, not here. An upload is scoped to a component that already exists — that is what the
              grant is signed against — so this step brings the component into being and the screen it opens is where
              the file goes.
            </Alert>
          )}

          {/* Said rather than hidden, because it is the difference
              between this dialog and every other write in the client: a
              field the platform reads for itself is absent here on
              purpose, not because nobody got to it. */}
          <Typography variant="caption" color="text.secondary">
            A create states what the type cannot do without, what an intent could set anyway, and what is chosen here
            and never changed. Anything the platform reads for itself — where a file landed, what it contains — is
            absent here and always will be.
          </Typography>

          {error && <Alert severity="error">{problemMessage(error, 'The component was not created.')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => submit().catch(shown)} disabled={isLoading || !typeName}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  )
}
